<template lang="pug">
.scanner-packages-for-driver-assignment-view#scanner-packages-for-driver-assignment-view
  .header-container
    b-button.bg-white(
      style="padding: 0.7rem; align-items: center; width: fit-content; display: flex; border-radius: 30px;"
      @click="goBack"
    )
      b-icon(style="color: #181818;" icon="arrow-left")
    span.header-title.pl-2.text-white Escanear paquetes para asignación
  .camera-select-row
    label.camera-select-label Cámara
    AppSelect(
      v-model="selectedCameraId"
      :options="cameraOptions"
    )
  .scan-zone
    b-tabs(
      v-model="activeTab"
      nav-wrapper-class="tab-item"
      nav-class="tab-item"
      pills content-class="scan-tab-content" justified style="height: 100%;")
      b-tab(title="Escanear código" style="height: 100%;" variant="success")
        div(v-if="renderedMode === MODES.QR && camerasReady" style="height: 100%;")
          .text-center(v-if="loader")
            b-spinner.float
          QrScanner(ref="qrScanner" style="height: 100%;" :cameraId="selectedCameraId" @scan="onDetect")
      b-tab(title="Modo foto" style="height: 100%;")
        div(v-if="renderedMode === MODES.CAMERA && camerasReady" style="height: 100%;")
          CameraCapture(ref="cameraCapture" style="height: 100%;" :cameraId="selectedCameraId" @photo-accepted="requestAssignmentItemFromImage")
  .bottom-container
    scanned-deliveries-table(:deliveries="scannedDeliveries" :images="images")
</template>

<script>
import { mapActions } from 'vuex'
import ScannedDeliveriesTable from '../components/assignment/ScannedDeliveriesTable.vue'
import CameraCapture from '../components/assignment/CameraCapture.vue'
import { getLabelCode, getLabelProvider } from '@/utils/scanningUtils'
import QrScanner from '../components/common/QrScanner.vue'
import AppSelect from '../components/UI/AppSelect.vue'
import * as Sentry from '@sentry/vue'
import { getCameras, pickDefaultCameraId } from '@/utils/cameraDevices'

const CAMERA_STORAGE_KEY = 'scanner_selected_camera_id'

const readStoredCamera = () => {
  const raw = localStorage.getItem(CAMERA_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && parsed.id) {
      return { id: parsed.id, label: parsed.label || null }
    }
  } catch (error) {}
  return { id: raw, label: null }
}

export default {
  components: {
    ScannedDeliveriesTable,
    CameraCapture,
    QrScanner,
    AppSelect
  },
  data() {
    const MODES = {
      QR: 0,
      CAMERA: 1
    }
    return {
      MODES,
      activeTab: MODES.QR,
      renderedMode: MODES.QR,
      scannedStrings: [],
      code: [],
      deliveries: [],
      requests: [],
      imageRequests: [],
      loader: false,
      success_audio: null,
      error_audio: null,
      cameras: [],
      camerasReady: false,
      selectedCameraId: readStoredCamera()?.id || null,
    }
  },
  async mounted() {
    this.success_audio = new Audio('success_scan.mp3')
    this.error_audio = new Audio('error_scan.wav')
    await this.loadCameras()
    this.camerasReady = true
  },
  computed: {
    scannedCount() {
      return this.requests.length
    },
    scannedDeliveries() {
      return this.requests.filter(request => !!request.delivery).map(request => request.delivery)
    },
    images() {
      return this.imageRequests.map(request => request.photoUrl)
    },
    cameraOptions() {
      return this.cameras.map((cam, idx) => ({
        value: cam.id,
        text: cam.label || `Cámara ${idx + 1}`
      }))
    }
  },
  watch: {
    selectedCameraId(newId) {
      if (newId && typeof newId === 'string') {
        const cam = this.cameras.find(c => c.id === newId)
        const stored = { id: newId, label: cam ? cam.label : null }
        localStorage.setItem(CAMERA_STORAGE_KEY, JSON.stringify(stored))
      }
    },
    async activeTab(newTab, oldTab) {
      if (newTab === oldTab) return

      try {
        if (oldTab === this.MODES.QR && this.$refs.qrScanner) {
          await this.$refs.qrScanner.stopScanner()
        } else if (oldTab === this.MODES.CAMERA && this.$refs.cameraCapture) {
          await this.$refs.cameraCapture.stopCamera()
        }
      } catch (err) {
        console.error('Error stopping camera/scanner on mode switch:', err)
      }

      this.renderedMode = null
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 200))

      this.renderedMode = newTab
    }
  },
  methods: {
    ...mapActions(['getDeliveryScanned', 'getDeliveryByLabelCode']),
    ...mapActions('assignment', ['createAssignmentRequestFromDeliveryId', 'createAssignmentRequestFromImage']),
    async loadCameras() {
      try {
        this.cameras = await getCameras()
        if (this.cameras.length) {
          const stored = readStoredCamera()
          const match = stored
            ? this.cameras.find(c => c.id === stored.id) ||
              (stored.label ? this.cameras.find(c => c.label && c.label === stored.label) : null)
            : null
          this.selectedCameraId = match ? match.id : pickDefaultCameraId(this.cameras)
        }
      } catch (error) {
        console.error('Error loading cameras:', error)
        Sentry.captureException(error, { tags: { context: 'loadCameras' } })
      }
    },
    goBack() {
      this.$router.push('/mis-envios')
    },
    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    },
    async requestAssignmentItemFromImage(photo) {
      try {
        this.loader = true
        const formData = new FormData()
        formData.append('photo', photo)
        const response = await this.createAssignmentRequestFromImage(formData)
        const item = response.data
        this.imageRequests.push(item)
        this.$bvToast.toast('Solicitud de asignación creada correctamente', {
          title: 'Éxito',
          variant: 'success',
        })
      } catch (error) {
        const response = error.response
        const errorMsg = response && response.data.message ? response.data.message : 'Ocurrió un error inesperado'
        // Report unexpected (non-HTTP) errors or server-side 5xx to Sentry
        if (!response || response.status >= 500) {
          Sentry.captureException(error, { tags: { context: 'requestAssignmentItemFromImage' } })
        }
        this.$bvToast.toast(errorMsg, {
          title: 'Error',
          variant: 'danger',
        })
      } finally {
        this.loader = false
      }
    },
    async onDetect(content) {
      try {
        this.loader = true

        if (this.scannedStrings.includes(content)) {
          return this.$bvToast.toast('Este paquete ya lo escaneaste.', {
            title: 'Error',
            variant: 'danger',
          })
        }

        this.scannedStrings.push(content)

        const response = await this.getDeliveryByLabelCode({ labelCode: getLabelCode(content), labelProvider: getLabelProvider(content) })

        const delivery = response.data

        const response2 = await this.createAssignmentRequestFromDeliveryId({ deliveryId: delivery.id })

        const item = response2.data

        this.requests.push(item)

        this.$bvToast.toast('Paquete escaneado correctamente', {
          title: 'Éxito',
          variant: 'success',
        })
        this.success_audio.play()
      } catch (error) {
        const response = error.response
        const errorMsg = response && response.data.message ? response.data.message : 'Ocurrió un error inesperado'
        // Report unexpected (non-HTTP) errors or server-side 5xx to Sentry
        if (!response || response.status >= 500) {
          Sentry.captureException(error, { tags: { context: 'onDetect' }, extra: { scannedContent: content } })
        }
        this.$bvToast.toast(errorMsg, {
          title: 'Error',
          variant: 'danger',
        })
        await this.error_audio.play()
      } finally {
        this.loader = false
      }
    },
  },
}

</script>

<style lang="scss" scoped>
.tab-content::v-deep {
  height: 100%;
}

.camera-select-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  background-color: #232323;
}

.camera-select-label {
  color: #ccc;
  font-size: 0.85rem;
  white-space: nowrap;
  margin: 0;
}

.header-container {
  margin: 0.5rem;
  display: flex;
  align-items: center;
}

.scan-zone {
  flex: 1;
  height: 0; /* flex: 1 sets flex-basis, height: 0 allows children to fill via height: 100% */
  min-height: 0;
}

.bottom-container {
  padding: 0.5rem;
}

.scanner-packages-for-driver-assignment-view {
  background-color: #191919;
  display: flex;
  flex-direction: column;
}

.tab-item {
  background-color: red !important;
}
</style>
<style lang="scss">
#scanner-packages-for-driver-assignment-view {

  .nav-link {
    background-color: rgb(201, 201, 201);

    margin: 0.5rem;
    height: 30px;
    font-size: 16px !important;

  }

  .nav-link.active {
    background-color: #c7d533 !important;
    color: var(--primary) !important;
  }
}

#scanned-success-deliveries-modal {


  .modal-dialog-centered {
    align-items: end !important;
  }

  .modal-content {
    border-radius: 16px 16px 0 0 !important;
    transition-timing-function: ease-out !important;
    transition-duration: 300ms !important;

    .modal-body {
      padding: 0.7rem 0.4rem;
      padding-bottom: 0;
    }

    .table th,
    .table td {
      padding: 0.4rem;
      vertical-align: middle;
      font-size: 15px;
      font-weight: 400;
    }
  }
}

.code-counter {
  color: #13475c;
}

.scan-tab-content {
  height: calc(100% - 50px);
}
</style>
